import { Button } from '@repo/ui/components/ui/button';
import { cn } from '@repo/ui/lib/utils';
import { Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import Comment from './comment';
import SelfComment, { SelfCommentModel } from './self-comment';

const RATING_MESSAGES = ['Not now', 'Neutral', 'Consider', 'Like', 'Love'];

type ReviewCandidateProps = {
  onCommentAddClick: (rating: number, comment?: string) => void;
  onSkipClick: (rating: number) => void;
  onUpdateClick: (commentId: string, comment: string) => void;
  processing?: boolean;
  rating?: number;
  userName?: string;
  userComment?: SelfCommentModel;
  editComment?: boolean;
};

export function ReviewCandidate({
  onCommentAddClick,
  onSkipClick,
  onUpdateClick,
  processing,
  rating: ratingValue,
  userName,
  userComment,
  editComment,
}: ReviewCandidateProps) {
  const { pathname } = useLocation();
  const [comment, setComment] = useState<SelfCommentModel | undefined>(
    userComment,
  );
  const [isCommentClosed, closeComment] = useState<boolean>(!editComment);
  const [rating, setRating] = useState<number | undefined>(ratingValue);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    setComment(undefined);
    closeComment(true);
    setHoveredIndex(null);
    setRating(ratingValue);
  }, [ratingValue, pathname]);

  useEffect(() => {
    closeComment(!editComment);
    setComment(userComment);
  }, [editComment, userComment]);

  return (
    <div className="bg-white grid p-4 gap-4 w-full">
      <div
        className={cn('hidden md:grid gap-2', {
          '!hidden': ratingValue && ratingValue > 0,
        })}
      >
        <div className="hidden md:block text-lg font-semibold">
          Your recommendation
        </div>
        <div className="hidden md:block text-sm font-light">
          Do you believe this candidate should become a finalist?
        </div>
      </div>
      <div className="flex gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Button
            key={i}
            variant="ghost"
            disabled={(ratingValue && ratingValue > 0) || processing}
            onMouseLeave={() => setHoveredIndex(null)}
            onMouseEnter={() => !rating && setHoveredIndex(i)}
            className="[&_svg]:!size-auto [&_svg]:!shrink-0 disabled:opacity-100 hover:bg-transparent size-fit p-0 w-1/5"
            onClick={() => {
              setRating(i + 1);
              closeComment(false);
            }}
          >
            <div className="grid grid-rows-[1fr,auto] place-items-center pt-6 pb-2 gap-2 h-fit">
              <Star
                size={24}
                strokeWidth={1}
                className={cn('text-zinc-700 fill-transparent', {
                  'fill-black': !!rating && i < rating,
                  'fill-zinc-300': hoveredIndex !== null && i <= hoveredIndex,
                  'fill-zinc-300 animate-pulse': processing,
                })}
              />
              <div className="font-light text-xs md:text-sm">
                {RATING_MESSAGES[i]}
              </div>
            </div>
          </Button>
        ))}
      </div>

      {comment && isCommentClosed && (
        <SelfComment
          comment={comment!}
          onEditClick={() => {
            closeComment(false);
            setComment(comment);
          }}
        />
      )}
      <Comment
        isCommentClosed={isCommentClosed}
        onCommentAddClick={(rating, comment) => {
          setComment(undefined);
          onCommentAddClick(rating, comment);
          closeComment(true);
        }}
        onCancelClick={(rating) => {
          setComment(undefined);
          onSkipClick(rating);
          closeComment(true);
        }}
        onUpdateClick={(commentId, comment) => {
          setComment(undefined);
          onUpdateClick(commentId, comment);
          closeComment(true);
        }}
        processing={processing}
        rating={rating}
        userComment={comment!}
        userName={userName || 'Anonymous'}
      />
    </div>
  );
}
